import { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Users, Plus, Loader2, Shield, Mail, Search, MoreHorizontal, Pause, Play, Ban, Archive, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import AuthHealthPanel from "@/components/admin/AuthHealthPanel";

const roles = [
  { value: "MASTER_ADMIN", label: "Master Admin" },
  { value: "BOOMA_ADMIN", label: "Booma Admin" },
  { value: "ADVERTISER_ADMIN", label: "Advertiser Admin" },
  { value: "ADVERTISER_USER", label: "Advertiser User" }
];

const roleColors = {
  MASTER_ADMIN: "bg-gradient-to-r from-primary to-purple-500 text-white",
  BOOMA_ADMIN: "bg-primary/10 text-primary border border-primary/50",
  ADVERTISER_ADMIN: "bg-green-500/10 text-green-500 border border-green-500/50",
  ADVERTISER_USER: "bg-secondary text-secondary-foreground"
};

export default function AdminUsers() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "ADVERTISER_USER",
    account_id: null
  });
  const [saving, setSaving] = useState(false);
  
  // Action modal states
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [usersRes, accountsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/accounts`, { headers })
      ]);
      setUsers(usersRes.data);
      setAccounts(accountsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...formData };
      if (payload.role === "MASTER_ADMIN" || payload.role === "BOOMA_ADMIN") {
        payload.account_id = null;
      }
      await axios.post(`${API}/admin/users`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User created successfully");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "ADVERTISER_USER",
      account_id: null
    });
  };

  // Action handlers
  const openActionModal = (user, action) => {
    setSelectedUser(user);
    setPendingAction(action);
    setActionModalOpen(true);
  };

  const closeActionModal = () => {
    setActionModalOpen(false);
    setSelectedUser(null);
    setPendingAction(null);
  };

  const handleUserStatusChange = async (userId, newStatus) => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/admin/users/${userId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User ${newStatus.toLowerCase()} successfully`);
      closeActionModal();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user status");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    setActionLoading(true);
    try {
      await axios.delete(`${API}/admin/users/${userId}?confirm=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User archived successfully");
      closeActionModal();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive user");
    } finally {
      setActionLoading(false);
    }
  };

  const confirmAction = () => {
    if (!selectedUser) return;
    
    switch (pendingAction) {
      case 'suspend':
        handleUserStatusChange(selectedUser.id, 'SUSPENDED');
        break;
      case 'disable':
        handleUserStatusChange(selectedUser.id, 'INACTIVE');
        break;
      case 'activate':
        handleUserStatusChange(selectedUser.id, 'ACTIVE');
        break;
      case 'delete':
        handleDeleteUser(selectedUser.id);
        break;
      default:
        closeActionModal();
    }
  };

  const getActionConfig = () => {
    switch (pendingAction) {
      case 'suspend':
        return {
          title: 'Suspend User',
          description: `Are you sure you want to suspend "${selectedUser?.name}"? They will be unable to log in.`,
          buttonText: 'Suspend User',
          variant: 'warning'
        };
      case 'disable':
        return {
          title: 'Disable User',
          description: `Are you sure you want to disable "${selectedUser?.name}"? This will deactivate their account.`,
          buttonText: 'Disable User',
          variant: 'warning'
        };
      case 'activate':
        return {
          title: 'Activate User',
          description: `Reactivate "${selectedUser?.name}"? They will be able to log in again.`,
          buttonText: 'Activate User',
          variant: 'default'
        };
      case 'delete':
        return {
          title: 'Archive User',
          description: `WARNING: This will archive "${selectedUser?.name}" and their data. This action cannot be undone.`,
          buttonText: 'Archive Forever',
          variant: 'destructive'
        };
      default:
        return { title: '', description: '', buttonText: '', variant: 'default' };
    }
  };

  // Filtered users
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.role?.toLowerCase().includes(query)
      );
    });
  }, [users, searchQuery]);

  const getAccountName = (accountId) => {
    const account = accounts.find(a => a.id === accountId);
    return account?.business_name || "N/A";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Users</h1>
          <p className="text-muted-foreground mt-1">
            Manage platform users and access
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="create-user-btn">
              <Plus className="mr-2 h-4 w-4" />
              Create User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Create New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  required
                  data-testid="user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(formData.role === "ADVERTISER_ADMIN" || formData.role === "ADVERTISER_USER") && (
                <div className="space-y-2">
                  <Label>Advertiser Account</Label>
                  <Select 
                    value={formData.account_id || "none"} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, account_id: value === "none" ? null : value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No account</SelectItem>
                      {accounts.map(account => (
                        <SelectItem key={account.id} value={account.id}>{account.business_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-user-btn">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Create User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Total Users</p>
                <p className="text-2xl font-bold mt-1">{users.length}</p>
              </div>
              <Users className="h-8 w-8 text-primary/60" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Admins</p>
                <p className="text-2xl font-bold mt-1">
                  {users.filter(u => u.role === "MASTER_ADMIN" || u.role === "BOOMA_ADMIN").length}
                </p>
              </div>
              <Shield className="h-8 w-8 text-purple-500/60" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Advertiser Admins</p>
                <p className="text-2xl font-bold mt-1">
                  {users.filter(u => u.role === "ADVERTISER_ADMIN").length}
                </p>
              </div>
              <div className="h-8 w-8 rounded-full bg-green-500/20 flex items-center justify-center">
                <div className="h-3 w-3 rounded-full bg-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Advertiser Users</p>
                <p className="text-2xl font-bold mt-1">
                  {users.filter(u => u.role === "ADVERTISER_USER").length}
                </p>
              </div>
              <Mail className="h-8 w-8 text-muted-foreground/60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Account health (round 8, W6). Three admin endpoints had no UI at all;
          this is the one that catches a person who cannot sign in. */}
      <AuthHealthPanel />

      {/* Search Bar */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search users by name, email, or role..." 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
            className="pl-10"
            data-testid="user-search-input"
          />
        </div>
      </div>

      {/* Users Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map(user => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-semibold">{user.name}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={roleColors[user.role]}>
                        {user.role?.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.account_id ? (
                        <span className="text-sm">{getAccountName(user.account_id)}</span>
                      ) : (
                        <span className="text-muted-foreground text-sm">Platform User</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        user.is_active === false || user.status === 'INACTIVE' 
                          ? 'bg-gray-500/10 text-gray-500 border-gray-500/30' 
                          : user.status === 'SUSPENDED' 
                            ? 'bg-red-500/10 text-red-500 border-red-500/30'
                            : 'bg-green-500/10 text-green-500 border-green-500/30'
                      }>
                        {user.status || (user.is_active !== false ? 'ACTIVE' : 'INACTIVE')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(user.created_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {(user.is_active !== false && user.status !== 'SUSPENDED' && user.status !== 'INACTIVE') ? (
                            <DropdownMenuItem onClick={() => openActionModal(user, 'suspend')}>
                              <Pause className="mr-2 h-4 w-4" />
                              Suspend User
                            </DropdownMenuItem>
                          ) : user.status === 'SUSPENDED' ? (
                            <DropdownMenuItem onClick={() => openActionModal(user, 'activate')}>
                              <Play className="mr-2 h-4 w-4" />
                              Reactivate User
                            </DropdownMenuItem>
                          ) : null}
                          {user.status !== 'INACTIVE' && user.is_active !== false && (
                            <DropdownMenuItem onClick={() => openActionModal(user, 'disable')}>
                              <Ban className="mr-2 h-4 w-4" />
                              Disable User
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => openActionModal(user, 'delete')}
                            className="text-destructive focus:text-destructive"
                          >
                            <Archive className="mr-2 h-4 w-4" />
                            Archive User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      {searchQuery ? 'No users match your search' : 'No users found'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Action Confirmation Modal */}
      <Dialog open={actionModalOpen} onOpenChange={setActionModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {pendingAction === 'delete' && <AlertTriangle className="h-5 w-5 text-destructive" />}
              {(pendingAction === 'suspend' || pendingAction === 'disable') && <AlertTriangle className="h-5 w-5 text-amber-500" />}
              {pendingAction === 'activate' && <Play className="h-5 w-5 text-green-500" />}
              {getActionConfig().title}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {getActionConfig().description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={closeActionModal} disabled={actionLoading}>
              Cancel
            </Button>
            <Button 
              variant={pendingAction === 'delete' ? 'destructive' : pendingAction === 'activate' ? 'default' : 'outline'}
              onClick={confirmAction}
              disabled={actionLoading}
              className={pendingAction === 'suspend' || pendingAction === 'disable' ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
            >
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {getActionConfig().buttonText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
